import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#F4F1EA] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin text-[#C85A3C] mx-auto mb-8" />
            <h1 className="font-serif-display text-4xl font-light text-[#1A1A1A]">Confirming your gift…</h1>
            <p className="mt-4 text-[#1A1A1A]/60">One moment while we verify your donation.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[#8A9A86] mx-auto mb-8" />
            <p className="text-xs uppercase tracking-[0.3em] text-[#C85A3C] mb-4">Thank You</p>
            <h1 className="font-serif-display text-5xl font-light text-[#1A1A1A] leading-tight">
              Your {amount ? `$${amount}` : ""} gift is on its way.
            </h1>
            <p className="mt-5 text-[#1A1A1A]/70 leading-relaxed">
              Your generosity helps keep a family together during a critical transition.
              We're deeply grateful.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="text-[#C85A3C] mx-auto mb-8" />
            <h1 className="font-serif-display text-4xl font-light text-[#1A1A1A]">Payment processing</h1>
            <p className="mt-4 text-[#1A1A1A]/60">
              Your payment is still being processed. You'll receive a confirmation shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={52} className="text-[#C85A3C] mx-auto mb-8" />
            <h1 className="font-serif-display text-4xl font-light text-[#1A1A1A]">We couldn't confirm your gift</h1>
            <p className="mt-4 text-[#1A1A1A]/60">Please try again or contact us if you were charged.</p>
          </>
        )}
        <Link
          to="/"
          data-testid="back-home-btn"
          className="mt-10 inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F4F1EA] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#C85A3C] transition-colors duration-300"
        >
          Back Home <Heart size={14} className="fill-current" />
        </Link>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
